import React, { useRef, useState, useEffect, useMemo } from "react";

import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";

import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { useTheme, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress"
import Menu from "@mui/material/Menu"
import IconButton from "components/@extended/IconButton";

import { ReactComponent as Alert } from "assets/images/icons/btn_header_alert.svg";
import { ReactComponent as AlertHover } from "assets/images/icons/btn_header_alert_hover.svg";
import { ReactComponent as AlertNoAlarm } from "assets/images/icons/btn_header_no_alert_hover.svg";
import { ReactComponent as AlertHoverNoAlarm } from "assets/images/icons/btn_header_no_alert.svg";

import { EVENTS_LOG_EVENTS_TYPE, EVENTS_LOG, EVENTS_LOG_MAP } from "const/eventsLog";

import { getPermissionCheckData } from "utils/getHierarchy";
import { formatTimestampByToday } from "utils/format";
import getEventsLogMessage from "utils/getEventsLogMessage";

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
    MenuListProps={{ disablePadding: true }}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    mt: 1.5,
    ml: 2.5,
    borderRadius: theme.shape.customBorderRadius[2],
    boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
    bgcolor: theme.palette.primary.white,
    maxWidth: 400,
    width: 400,
    maxHeight: 500,
    border: `1px solid ${theme.palette.primary.whiteSecound}`,
    position: "relative",
    overflow: "visible",
    padding: 0,

    "&::before": {
      content: '""',
      position: "absolute",
      top: "-12px",
      right: 20,
      width: 0,
      height: 0,
      borderLeft: "8px solid transparent",
      borderRight: "8px solid transparent",
      borderBottom: `12px solid ${theme.palette.primary.white}`,
      filter:
        "drop-shadow(-2px -1px 2px rgba(0, 0, 0, 0.05)) drop-shadow(2px -1px 2px rgba(0, 0, 0, 0.05))",
      zIndex: 1,
    },
  },
}));

const StyledMenuContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.white,
  borderRadius: theme.shape.customBorderRadius[2],
  padding: '20px',
  width: "400px",
  maxHeight: "500px",
  overflowY: "auto",
  overflowX: "hidden",

  "&::-webkit-scrollbar": {
    width: 5,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 8,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: theme.palette.primary.mainHover,
    cursor: "ew-resize"
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.primary.greyThread,
    borderRadius: theme.shape.customBorderRadius[0],
  },
  "&::-webkit-scrollbar-button": {
    display: "none",
    width: 0,
    height: 0,
  },
}));

const NotificationTypography = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[1],
}));

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const Notification = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const handleToggle = () => { setOpen((prevOpen) => !prevOpen) };

  const [loading, setLoading] = useState(false)
  const [notificationUuid, _setNotificationUuid] = useState(uuidv4());
  // 因為 useAuthScope 屬於 父層想呼叫子層的 hook 所以呼叫不到
  // 又要避免出現 response 回來又取代前一個 response
  // 所以用 currentProcess 控制
  const [currentProcess, setCurrentProcess] = useState("fetchNotificationList")
  const [permissionState, setPermissionState] = useState({
    theLatestPermissionData: {},
    theLatestSelectBreadcrumbs: {},
    validatePermission: {}
  })
  const [notificationData, setNotificationData] = useState([])
  const [selectedNotificationData, setSelectedNotificationData] = useState({})
  const isEmptyNotification = useMemo(() => !notificationData?.length, [notificationData]);

  const mockupData = useSelector((state) => state.events.data);

  // 因為父層想呼叫子層的 hook 所以呼叫不到
  // 所以用此方法

  const ws = useWebSocket();
  const { token } = useAuth();

  function fetchNotificationsData() {
    const requestNotifications = {
      jsonrpc: "2.0",
      id: notificationUuid,
      method: "read",
      params: {
        resource: "/me/notifications",
        accessToken: token,
      },
    };
    ws.send(JSON.stringify(requestNotifications));
  }

  function handleNotificationsData(msg) {
    if (msg.id !== notificationUuid) return;
    setNotificationData(msg?.result?.data || [])
  }

  function handleEventData(msg) {
    // 當 user 有 acknowledge event log 時, 要重取 notification list.
    const resourcePath = msg?.result?.resource ?? "";
    if (resourcePath.includes("events/acknowledge")) fetchNotificationsData();
  }

  function handleNotificationClick(selectedData) {
    // 之前 Hierarchy 這段 mockup redux 沒做
    // 所以 mockup 跳轉先不做
    if (checkMockup === "true" || currentProcess !== "fetchNotificationList") return;
    setSelectedNotificationData(selectedData)
    setCurrentProcess("fetchPermission")
  }

  useEffect(() => {
    if (checkMockup === "true"){
      setNotificationData(mockupData);
    } else {
      fetchNotificationsData();
    }
  }, [])

  useEffect(() => {
    if (open === true) {
      if (checkMockup === "false" && token) {
        setLoading(true)
        fetchNotificationsData()
      }
      // mockup 拿資料
      if (checkMockup === "true") {
        setNotificationData(mockupData)
      }
    }
  }, [open])

  useEffect(() => {
    const fetchPermissionAndProcess = async () => {
      if (currentProcess === "fetchPermission") {
        try {
          const permissionCheckData = await getPermissionCheckData({
            ws,
            token,
            validatePermissionData: selectedNotificationData
          });
          setPermissionState(permissionCheckData)
          setCurrentProcess("navigate")
        } catch (err) {
          console.error("getPermissionCheckResult error:", err);
        }
      }

      if (currentProcess === "navigate") {
        const selectBreadcrumbs = permissionState.theLatestSelectBreadcrumbs
        localStorage.setItem(
          "selectBreadcrumbs",
          JSON.stringify(selectBreadcrumbs)
        );

        const standaloneOrg =
          Object.keys(selectBreadcrumbs.realm).length > 0 &&
          selectBreadcrumbs.hasManageOrg === false &&
          Object.keys(selectBreadcrumbs.orgs).length === 0 &&
          Object.keys(selectBreadcrumbs.site).length === 0;

        const nonStandaloneOrg =
          Object.keys(selectBreadcrumbs.realm).length > 0 &&
          Object.keys(selectBreadcrumbs.orgs).length > 0 &&
          Object.keys(selectBreadcrumbs.site).length === 0;

        if (standaloneOrg || nonStandaloneOrg) {
          navigate("/org/events");
        } else {
          navigate("/site/events");
        }

        setCurrentProcess("fetchNotificationList");
        setPermissionState({
          theLatestPermissionData: {},
          theLatestSelectBreadcrumbs: {},
          validatePermission: {}
        })
        setSelectedNotificationData({});
        handleToggle();
      }
    };

    fetchPermissionAndProcess();
  }, [currentProcess]);

  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      const msg = JSON.parse(event.data);

      handleNotificationsData(msg);
      handleEventData(msg);
      setLoading(false);
    };

    ws.addEventListener("message", handleMessage);

    return () => {
      ws.removeEventListener("message", handleMessage);
    };
  }, [ws]);

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={{
          color: "text.primary",
          bgcolor: "transparent",
          borderRadius: "24px",
          "&:hover": {
            bgcolor: "transparent",
          },
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            ...(open && {
              bgcolor: theme.palette.primary.lightBlue,
              borderRadius: theme.shape.customBorderRadius[3],
            }),
          }}
        >
          {
            isHovered || open ?
              (isEmptyNotification ? <AlertNoAlarm /> : <AlertHover />) :
              (isEmptyNotification ? <AlertHoverNoAlarm /> : <Alert />)
          }
        </Box>
      </IconButton>

      <StyledMenu
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
      >
        <StyledMenuContent>
          {loading === true && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px", }}>
              <CircularProgress />
            </Box>
          ) || notificationData.map((item, index) => (
            <Box
              sx={{
                cursor: 'pointer',
                "&:hover .notification-text": {
                  color: theme.palette.primary.mainHover,
                },
              }}
              key={item.id}
              onClick={() => handleNotificationClick(item)}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                }}
              >
                <NotificationTypography className="notification-text" sx={{ color: theme.palette.primary.greyPrimary }}>
                  {EVENTS_LOG_EVENTS_TYPE[item.eventType]}
                </NotificationTypography>

                <NotificationTypography className="notification-text" sx={{ color: theme.palette.primary.greyPrimary }}>
                  {formatTimestampByToday(item.createdAt)}
                </NotificationTypography>
              </Box>

              <NotificationTypography
                className="notification-text"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: theme.typography.customFontWeight[1],
                  marginBottom: '5px'
                }}>
                {getEventsLogMessage(item, EVENTS_LOG, EVENTS_LOG_MAP)}
              </NotificationTypography>

              {index !== notificationData.length - 1 && <Divider />}
            </Box>
          ))}
        </StyledMenuContent>
      </StyledMenu>
    </Box>
  );
}

export default Notification
